import { onErrorMessage, useValidationErrors } from '@/helpers'
import { AddressProps, QuotationRequestDeliveryEnum } from '@/types'
import { useEffect, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useTranslation } from 'react-i18next'

import {
  Button,
  DrawerStepperProgress,
  Label,
  Select,
  Text,
} from '@mercai/clever'

import {
  QUERIES_ADMIN,
  useBffAddressList,
  useBffCreateAddress,
  useEditAddress,
  useUpdateQuotation,
  useUpdateQuotationRequest,
} from '@/services'

import { addressSchema } from './address-drawer-form.constants'
import { AddressDrawerFormProps } from './address-drawer-form.type'

import { AddressList } from '../address-list'

import { AddressForm } from '@/components'
import styles from './address-drawer-form.module.css'

export const AddressDrawerForm = ({
  quotationRequest,
  quotation,
  isOpen,
  onHandleOpen,
  setAddress,
  onBack,
  isCreate,
  setDeliveryOption,
}: AddressDrawerFormProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  const queryClient = useQueryClient()

  const [data, setData] = useState<Partial<AddressProps>>()

  const [selectedAddress, setSelectedAddress] =
    useState<Partial<AddressProps>>()

  const [internalDeliveryOption, setInternalDeliveryOption] =
    useState<keyof typeof QuotationRequestDeliveryEnum>('receive_at_address')

  const profileUuid =
    quotationRequest?.buyerProfile?.uuid ||
    quotationRequest?.buyerProfileUuid ||
    quotation?.buyerProfile?.uuid ||
    quotation?.buyerProfileUuid ||
    ''

  const { data: dataAddresses } = useBffAddressList(
    { profile_Uuid: profileUuid },
    !!profileUuid,
  )

  const { errors, isValid, onHandleListenErrors } = useValidationErrors(
    addressSchema(t),
    data,
  )

  const { mutateAsync: mutateAsyncNewAddress, isPending: isPendingNewAddress } =
    useBffCreateAddress()

  const {
    mutateAsync: mutateAsyncEditAddress,
    isPending: isPendingEditAddress,
  } = useEditAddress(data?.uuid || '')

  const {
    mutateAsync: mutateAsyncUpdateQuotationRequest,
    isPending: isPendingQuotationRequest,
  } = useUpdateQuotationRequest(quotationRequest?.uuid || '')

  const {
    mutateAsync: mutateAsyncUpdateQuotation,
    isPending: isPendingQuotation,
  } = useUpdateQuotation(quotation?.uuid || '')

  const onSubmitNewAddress = async ({
    onNavigateToPreviousStep,
  }: {
    onNavigateToPreviousStep: () => void
  }) => {
    if (!isValid) {
      return onHandleListenErrors(true)
    }

    onHandleListenErrors(false)

    const payload: Partial<AddressProps> = {
      ...data,
      profile: data?.profile || profileUuid,
      uuid: undefined,
      updatedAt: undefined,
      createdAt: undefined,
    }

    const response = !data?.uuid
      ? await mutateAsyncNewAddress(payload)
      : await mutateAsyncEditAddress(payload)

    if (response.success) {
      onNavigateToPreviousStep()

      setSelectedAddress(response?.data as Partial<AddressProps>)

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.BFF_ADDRESS.module],
      })
    }
  }

  const onHandleNewAddress = ({
    onNavigateToNextStep,
  }: {
    onNavigateToNextStep: (route: string) => void
  }) => {
    onNavigateToNextStep('addressForm')
  }

  const onSelectDeliveryOption = (
    value: keyof typeof QuotationRequestDeliveryEnum,
  ) => {
    setInternalDeliveryOption(value)

    if (value === 'pick_up') {
      setSelectedAddress({})
    }
  }

  const onSubmitCompleted = async () => {
    let response = {
      success: true,
    }

    if (!isCreate) {
      response = await (quotation
        ? mutateAsyncUpdateQuotation({
            ...quotation,
            buyerProfileUuid: profileUuid,
            deliveryAddressUuid: selectedAddress?.uuid,
            deliveryAddress: undefined,
            products: quotation?.products?.map((product) => ({
              ...product,
              catalogProductUuid: product?.catalogProduct?.uuid,
            })),
          })
        : mutateAsyncUpdateQuotationRequest({
            ...quotationRequest,
            buyerProfileUuid: profileUuid,
            addressUuid:
              quotationRequest?.status === 'approved_for_quotation'
                ? selectedAddress?.uuid
                : internalDeliveryOption === 'pick_up'
                  ? (null as unknown as string)
                  : selectedAddress?.uuid,
            address: undefined,
            deliveryOption:
              quotationRequest?.status !== 'approved_for_quotation'
                ? internalDeliveryOption
                : 'receive_at_address',
            items: quotationRequest?.items?.map((product) => ({
              ...product,
              catalogProductUuid: product?.catalogProduct?.uuid,
            })),
          }))
    }

    if (response.success) {
      onHandleOpen()
      setAddress(selectedAddress as Partial<AddressProps>)
      setDeliveryOption?.(internalDeliveryOption)

      if (!quotation) {
        queryClient.invalidateQueries({
          queryKey: [
            QUERIES_ADMIN.QUOTATION_REQUEST.module,
            quotationRequest?.uuid,
          ],
        })
      }
    } else {
      onErrorMessage(
        t('addressDrawerFormErrorTitle'),
        t('addressDrawerFormErrorMessage'),
      )
    }
  }

  const onHandleEditAddress = (
    address: AddressProps,
    onNavigateToNextStep: (route: string) => void,
  ) => {
    setData(address)
    onNavigateToNextStep('addressForm')
  }

  useEffect(() => {
    if (quotationRequest?.address) {
      setSelectedAddress(quotationRequest?.address)
    }

    if (quotationRequest?.deliveryOption) {
      setInternalDeliveryOption(quotationRequest?.deliveryOption)
    }

    if (quotation?.deliveryAddress) {
      setSelectedAddress(quotation?.deliveryAddress)
    }

    if (!isOpen) {
      setSelectedAddress({})
      setInternalDeliveryOption('receive_at_address')
      setData({})
    }
  }, [quotationRequest, quotation, isOpen])

  return (
    <DrawerStepperProgress
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      initialStep="root"
      translations={{ optional: '' }}
      title={t('addressDrawerFormTitle')}
      description={t('addressDrawerFormDescription')}
      steps={{
        root: {
          isOptional: true,
          backNavigate: 'root',
          component: ({ onNavigateToNextStep }) => (
            <>
              {quotationRequest &&
                quotationRequest?.status !== 'approved_for_quotation' && (
                  <div className={styles['select-delivery-option']}>
                    <Text
                      variant="text-large-bold"
                      className={styles['select-delivery-option-title']}
                    >
                      {t('addressListDeliveryOptionTitle')}
                    </Text>
                    <Label>{t('addressListDeliveryOptionLabel')}</Label>
                    <Select
                      value={internalDeliveryOption}
                      options={[
                        ...Object.keys(QuotationRequestDeliveryEnum).map(
                          (item) => ({
                            label: tEnums(
                              `logistic_shipment_delivery_option_${item}`,
                            ),
                            value: item,
                          }),
                        ),
                      ]}
                      onChange={(value) => {
                        onSelectDeliveryOption(
                          value as QuotationRequestDeliveryEnum,
                        )
                      }}
                      placeholder={t('orderDataDeliveryOptionPlaceholder')}
                    />
                  </div>
                )}
              {(quotation ||
                quotationRequest?.status === 'approved_for_quotation' ||
                !internalDeliveryOption ||
                internalDeliveryOption === 'receive_at_address') && (
                <AddressList
                  onClickNewAddress={() =>
                    onHandleNewAddress({ onNavigateToNextStep })
                  }
                  onChangeAddressSelected={(address) =>
                    setSelectedAddress(address)
                  }
                  addressSelected={selectedAddress as AddressProps}
                  addresses={dataAddresses?.data?.results || []}
                  onEditAddress={(address) =>
                    onHandleEditAddress(address, onNavigateToNextStep)
                  }
                />
              )}
            </>
          ),
          footer: () => (
            <>
              <Button variant="secondary" onClick={onHandleOpen}>
                {t('drawerFormCancel')}
              </Button>

              {onBack && (
                <Button variant="success-light" onClick={onBack}>
                  {t('drawerFormBack')}
                </Button>
              )}

              <Button
                onClick={onSubmitCompleted}
                isActive={!isPendingQuotationRequest || !isPendingQuotation}
                disabled={isPendingQuotationRequest || isPendingQuotation}
              >
                {t('drawerFormSubmit')}
              </Button>
            </>
          ),
        },
        addressForm: {
          isOptional: false,
          backNavigate: 'root',
          component: () => (
            <AddressForm
              onChangeValues={setData}
              errors={errors}
              values={data}
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <>
              <Button variant="secondary" onClick={onNavigateToPreviousStep}>
                {t('drawerFormBack')}
              </Button>
              <Button
                onClick={() => onSubmitNewAddress({ onNavigateToPreviousStep })}
                disabled={isPendingNewAddress || isPendingEditAddress}
                isActive={!isPendingNewAddress && !isPendingEditAddress}
              >
                {t('drawerFormSubmit')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
